import React from 'react';

import { STEP_VALIDATORS, type StepName as DomainStepName } from '@eduzz-registry-360/validators';

// Frontend extras (pure UI / transition screens) that are not part of the
// backend's `STEP_VALIDATORS` because they have no persisted data.
type ExtraStepName = 'pending-verification' | 'the-end' | 'error' | 'loading';
export type StepName = DomainStepName | ExtraStepName;

const components: Record<StepName, React.LazyExoticComponent<React.ComponentType>> = {
  'legal-entity-relation': React.lazy(async () => {
    const module = await import('../steps/LegalEntityRelation');
    return { default: module.LegalEntityRelation };
  }),
  'document-not-found-capture': React.lazy(async () => {
    const module = await import('../steps/DocumentNotFoundCapture');
    return { default: module.DocumentNotFoundCapture };
  }),
  'occupation-natural': React.lazy(async () => {
    const module = await import('../steps/OccupationNatural');
    return { default: module.OccupationNatural };
  }),
  'occupation-partner': React.lazy(async () => {
    const module = await import('../steps/OccupationPartner');
    return { default: module.OccupationPartner };
  }),
  'occupation-representative': React.lazy(async () => {
    const module = await import('../steps/OccupationRepresentative');
    return { default: module.OccupationRepresentative };
  }),
  'income-natural': React.lazy(async () => {
    const module = await import('../steps/IncomeNatural');
    return { default: module.IncomeNatural };
  }),
  'income-partner': React.lazy(async () => {
    const module = await import('../steps/IncomePartner');
    return { default: module.IncomePartner };
  }),
  'income-representative': React.lazy(async () => {
    const module = await import('../steps/IncomeRepresentative');
    return { default: module.IncomeRepresentative };
  }),
  'legal-entity-income': React.lazy(async () => {
    const module = await import('../steps/LegalEntityIncome');
    return { default: module.LegalEntityIncome };
  }),
  'pep-natural': React.lazy(async () => {
    const module = await import('../steps/PepNatural');
    return { default: module.PepNatural };
  }),
  'pep-partner': React.lazy(async () => {
    const module = await import('../steps/PepPartner');
    return { default: module.PepPartner };
  }),
  'pep-representative': React.lazy(async () => {
    const module = await import('../steps/PepRepresentative');
    return { default: module.PepRepresentative };
  }),
  'face-detection': React.lazy(async () => {
    const module = await import('../steps/FaceDetection');
    return { default: module.FaceDetection };
  }),
  'representative-document-file': React.lazy(async () => {
    const module = await import('../steps/RepresentativeDocumentFile');
    return { default: module.RepresentativeDocumentFile };
  }),
  'address': React.lazy(async () => {
    const module = await import('../steps/Address');
    return { default: module.Address };
  }),
  'bank-account': React.lazy(async () => {
    const module = await import('../steps/BankAccount');
    return { default: module.BankAccount };
  }),
  'terms': React.lazy(async () => {
    const module = await import('../steps/Terms');
    return { default: module.Terms };
  }),
  'document-natural': React.lazy(async () => {
    const module = await import('../steps/DocumentNatural');
    return { default: module.DocumentNatural };
  }),
  'document-partner': React.lazy(async () => {
    const module = await import('../steps/DocumentPartner');
    return { default: module.DocumentPartner };
  }),
  'document-representative': React.lazy(async () => {
    const module = await import('../steps/DocumentRepresentative');
    return { default: module.DocumentRepresentative };
  }),
  'phone-natural': React.lazy(async () => {
    const module = await import('../steps/PhoneNatural');
    return { default: module.PhoneNatural };
  }),
  'phone-representative': React.lazy(async () => {
    const module = await import('../steps/PhoneRepresentative');
    return { default: module.PhoneRepresentative };
  }),
  'phone-partner': React.lazy(async () => {
    const module = await import('../steps/PhonePartner');
    return { default: module.PhonePartner };
  }),
  'social-contract-partner': React.lazy(async () => {
    const module = await import('../steps/SocialContractPartner');
    return { default: module.SocialContractPartner };
  }),
  'social-contract-representative': React.lazy(async () => {
    const module = await import('../steps/SocialContractRepresentative');
    return { default: module.SocialContractRepresentative };
  }),
  'error': React.lazy(async () => {
    const module = await import('../steps/Error');
    return { default: module.Error };
  }),
  'loading': React.lazy(async () => {
    const module = await import('../steps/Loading');
    return { default: module.Loading };
  }),
  'pending-verification': React.lazy(async () => {
    const module = await import('../steps/PendingVerification');
    return { default: module.PendingVerification };
  }),
  'the-end': React.lazy(async () => {
    const module = await import('../steps/TheEnd');
    return { default: module.TheEnd };
  })
};

// `validator` is undefined for pure-UI steps (PendingVerification, TheEnd).
export const allSteps = Object.fromEntries(
  (Object.keys(components) as StepName[]).map(name => [
    name,
    {
      component: components[name],
      validator: (STEP_VALIDATORS as Partial<typeof STEP_VALIDATORS>)[name as DomainStepName]
    }
  ])
) as Record<
  StepName,
  {
    component: React.LazyExoticComponent<React.ComponentType>;
    validator: (typeof STEP_VALIDATORS)[DomainStepName] | undefined;
  }
>;
